// 같은 브라우저의 다른 탭이 견적을 저장했음을 알리는 신호(BroadcastChannel) — 옛 내용을 이어 고치지 않게.

import { LOCK_REASONS, lockedNoteOf, strongerLockOf, type CopyHead, type LockReason } from "../lock-reason";
import { SUPERSEDED_NOTE } from "./restore-offer";

export const QUOTE_SAVED_CHANNEL = "buildpy:quote-saved";

// 신호 종류 — 저장은 새로 불러오라는 띠, 나머지(발송·수주·이탈·새 버전)는 그 까닭으로 편집 잠김.
export type SavedKind = "saved" | LockReason;

// 저장 신호는 번호로 새것을 가린다 · 잠김 신호는 초안으로 되돌아가지 않으므로 번호 없이도 참(새 버전은 원본 번호가 안 오른다).
export type QuoteSavedSignal =
  | { quote_id: string; rev: number; kind?: "saved" }
  // 발송 신호의 서버 머리(head) — 받는 탭이 잠글 때 복사 비교 기준으로 쓴다.
  | { quote_id: string; rev?: number; kind: LockReason; head?: CopyHead };

// core 는 DOM 타입이 없어 쓰는 부분만 적는다 — 웹에선 BroadcastChannel 그대로.
export interface SavedChannel {
  postMessage(message: unknown): void;
  close(): void;
  onmessage: ((event: { data: unknown }) => void) | null;
}

// 신호에 실린 잠김 까닭 — 아는 값일 때만.
const lockKindOf = (data: unknown): LockReason | null => {
  const kind = typeof data === "object" && data !== null ? (data as { kind?: unknown }).kind : undefined;
  return LOCK_REASONS.find((r) => r === kind) ?? null;
};

// 이 견적의 저장 신호(잠김 신호 아님)이고 이 탭이 아는 번호보다 새 저장이면 true.
export const savedElsewhereOf = (data: unknown, quoteId: string, rev: number): boolean => {
  if (typeof data !== "object" || data === null || lockKindOf(data) !== null) return false;
  const signal = data as { quote_id?: unknown; rev?: unknown };
  return signal.quote_id === quoteId && typeof signal.rev === "number" && signal.rev > rev;
};

// 받은 신호로 이 탭이 할 일 — 이 견적의 잠김 신호면 그 까닭(번호 무관) · 새 번호의 저장이면 saved(띠) · 아니면 none.
export const savedSignalActionOf = (data: unknown, quoteId: string, rev: number): "none" | SavedKind => {
  const lock = lockKindOf(data);
  if (lock !== null) return (data as { quote_id?: unknown }).quote_id === quoteId ? lock : "none";
  return savedElsewhereOf(data, quoteId, rev) ? "saved" : "none";
};

const isHead = (v: unknown): v is CopyHead => {
  if (typeof v !== "object" || v === null) return false;
  const h = v as Record<string, unknown>;
  const numOrNull = (x: unknown) => x === null || typeof x === "number";
  return (
    typeof h.title === "string" &&
    typeof h.mgmt_fee_pct === "string" &&
    typeof h.extra_notice === "string" &&
    numOrNull(h.area_pyeong) &&
    numOrNull(h.rounding) &&
    (h.area_m2 === undefined || numOrNull(h.area_m2)) &&
    Array.isArray(h.conditions) &&
    h.conditions.every((c) => typeof c === "string")
  );
};

// 신호에 실린 서버 머리 — 모양이 맞을 때만(없거나 깨졌으면 undefined → 잠김만 하고 기준은 그대로).
export const signalHeadOf = (data: unknown): CopyHead | undefined => {
  const head = typeof data === "object" && data !== null ? (data as { head?: unknown }).head : undefined;
  return isHead(head) ? head : undefined;
};

// 신호를 받은 뒤 기억할 다른 탭의 번호 — 새 저장이면 둘 중 큰 번호, 아니면 그대로(null = 배너 없음).
export const elsewhereRevOf = (current: number | null, data: unknown, quoteId: string, rev: number): number | null =>
  savedElsewhereOf(data, quoteId, rev) ? Math.max(current ?? 0, (data as { rev: number }).rev) : current;

// 이 탭의 번호가 신호 번호 이상이 되면(최신본을 받았으면) 배너를 끈다.
export const pendingSignalRevOf = (signal: number | null, rev: number): number | null =>
  signal !== null && rev >= signal ? null : signal;

// 채널을 연다 — BroadcastChannel 이 없는 환경(서버·옛 브라우저)은 null.
export const openSavedChannel = (): SavedChannel | null =>
  typeof BroadcastChannel === "undefined" ? null : (new BroadcastChannel(QUOTE_SAVED_CHANNEL) as unknown as SavedChannel);

export const postSaved = (channel: SavedChannel | null, signal: QuoteSavedSignal): void => {
  try {
    channel?.postMessage(signal);
  } catch {
    // 닫힌 채널 — 알림은 덤이라 조용히 넘긴다.
  }
};

// 편집기 밖(상세의 발송·확정·새 버전, 가견적 저장)에서 견적을 바꾼 뒤 같은 브라우저의 편집기 탭에 알린다 — 열어 보내고 닫는다.
export const announceSaved = (signal: QuoteSavedSignal, open: () => SavedChannel | null = openSavedChannel): void => {
  const channel = open();
  postSaved(channel, signal);
  channel?.close();
};

// stale 로 막힌 기준 번호 — 명시 저장은 서버 문구를 싣고, 자동저장은 문구 없는 표식만 남긴다.
export interface StaleSave {
  rev: number;
  message?: string;
}

// 자동저장 응답 뒤 막힌 기록 — stale 이면 보낸 번호의 표식을 남긴다(같은 번호의 서버 문구는 그대로 둔다).
export const staleMarkOf = (resultStale: boolean, sentRev: number, staleSave: StaleSave | null): StaleSave | null =>
  resultStale && staleSave?.rev !== sentRev ? { rev: sentRev } : staleSave;

// 자동저장 응답 뒤 stale 표시 — 같은 번호로 저장이 이미 stale 로 막혔으면 늦게 닿은 stale=false 로 풀지 않는다.
export const autosaveStaleOf = (resultStale: boolean, sentRev: number, staleSave: StaleSave | null): boolean =>
  resultStale || (staleSave !== null && staleSave.rev === sentRev);

// 기준 번호가 바뀌면(저장 성공·되살리기) 옛 번호의 막힌 기록은 버린다.
export const staleSaveAfterSyncOf = (staleSave: StaleSave | null, rev: number): StaleSave | null =>
  staleSave !== null && staleSave.rev === rev ? staleSave : null;

// 번호를 따로 들고 있지 않은 화면(가견적)이 기억할 신호 — 잠김이 저장 띠를 이기고, 잠김끼리는 더 뒤 단계로.
export const strongerSignalOf = (current: SavedKind | null, incoming: SavedKind): SavedKind => {
  if (incoming === "saved") return current ?? "saved";
  return current === null || current === "saved" ? incoming : strongerLockOf(current, incoming);
};

// 그 신호의 띠 문구.
export const signalNoteOf = (kind: SavedKind): string => (kind === "saved" ? SAVED_ELSEWHERE_NOTE : lockedNoteOf(kind));

export const SAVED_ELSEWHERE_NOTE = "다른 탭에서 이 견적이 저장됐습니다 — 이 화면은 저장 전 내용입니다.";
export const STALE_NOTE = "이 견적을 연 뒤에 누가 저장했습니다. 새로고침해 확인한 뒤 저장하세요.";
// 가견적 편집기 전용 — 상세견적과 달리 초안 자동저장·이어쓰기가 없어 새로 불러오면 입력이 사라진다.
export const ROUGH_STALE_NOTE = "이 견적을 연 뒤에 누가 저장했습니다. 새로 불러오면 지금 편집한 내용은 사라집니다 — 필요한 값은 먼저 옮겨 두세요.";
// 가견적 저장이 stale 로 막혔을 때 토스트 — 가견적엔 덮어쓰기 길이 없어 서버 문구 대신 이것을 띄운다.
export const ROUGH_STALE_TOAST = "이 견적을 연 뒤에 누가 저장했습니다 — 새로 불러온 뒤 다시 저장해 주세요";

// 서버에 안 남은 편집이라는 꼬리 — 잠김 띠·밀림 문구가 같이 쓴다.
export const localOnlyNoteOf = (note: string): string => `${note}. 지금 편집은 이 화면에만 있습니다.`;

// 문장 끝 구두점이 없으면 마침표를 붙인다 — 이어 붙인 다음 문장과 붙어 읽히지 않게.
export const sentenceOf = (text: string): string => (/[.!?。]$/.test(text.trimEnd()) ? text : `${text.trimEnd()}.`);

// stale 띠 문구 — 저장이 막힌 서버 문구가 먼저, 그 뒤 자동저장까지 밀렸으면 밀림 문구도 잇는다.
export const staleNoteOf = (staleSave: StaleSave | null, superseded: boolean): string => {
  const message = staleSave?.message;
  if (message === undefined) return superseded ? localOnlyNoteOf(SUPERSEDED_NOTE) : STALE_NOTE;
  return superseded ? `${sentenceOf(message)} ${localOnlyNoteOf(SUPERSEDED_NOTE)}` : message;
};

// 항상 마운트된 live 영역(role=status)의 글 — 잠김은 받은 lockNote(토스트가 이미 읽었으면 비움), 명시 저장의 서버 문구는 토스트가 읽으므로 그 뒤 새로 생긴 밀림 문구만.
export const conflictNoteOf = (s: {
  locked: boolean;
  lockNote?: string;
  stale: boolean;
  superseded: boolean;
  staleSave: StaleSave | null;
  savedElsewhere: boolean;
}): string => {
  if (s.locked) return s.lockNote ?? "";
  if (s.stale) {
    if (s.staleSave?.message === undefined) return staleNoteOf(s.staleSave, s.superseded);
    return s.superseded ? localOnlyNoteOf(SUPERSEDED_NOTE) : "";
  }
  return s.savedElsewhere ? SAVED_ELSEWHERE_NOTE : "";
};

// 가견적 live 영역 글 — 잠김 신호가 먼저, 저장 막힘은 토스트(서버 문구)에 없는 「새로 불러오면 사라짐」 경고, 그다음 다른 탭 저장.
export const roughConflictNoteOf = (stale: boolean, elsewhere: SavedKind | null): string => {
  if (elsewhere !== null && elsewhere !== "saved") return signalNoteOf(elsewhere);
  if (stale) return ROUGH_STALE_NOTE;
  return elsewhere === "saved" ? SAVED_ELSEWHERE_NOTE : "";
};
